import { Link, useForm } from "@inertiajs/react"
import { useState } from "react"
import type { FormEvent } from "react"

import AdminLayout from "../../../shared/AdminLayout"
import { csrfToken } from "../../../shared/csrf"

interface AdminFlightFormLabels {
  // t022 (FR-7.1, FR-7.8): the retired «Кто летит» `<select>`'s own
  // `whoPlaceholder` key went with it in the previous child; `whoLabel`
  // survives and now feeds the pill row's own `<legend>` below.
  whoLabel: string
  flightNumberLabel: string
  originLabel: string
  destinationLabel: string
  departsLabel: string
  departsDateAria: string
  departsTimeAria: string
  departsHint: string
  arrivesLabel: string
  arrivesDateAria: string
  arrivesTimeAria: string
  arrivesHint: string
  noteLabel: string
  optional: string
  save: string
  delete: string
  deleteConfirm: string
}

interface AdminFlightFormProps {
  heading: string
  backHref: string
  backLabel: string
  // The collection route for a new flight (POST) or the flight's own
  // address (PATCH) -- distinct from `backHref`, the `AdminContactForm`/
  // `AdminStayForm` shape.
  submitHref: string
  // `null` on the new-flight form -- absent, not merely hidden, is what
  // keeps «Удалить рейс» off that screen (FR-6.5) and also tells this
  // component which HTTP verb `submitHref` expects.
  deleteHref: string | null
  flight: {
    // `null` on the new-flight form; the four boundary fields arrive
    // machine-format (ISO date, zero-padded 24-hour time) -- exactly
    // what `<input type="date">`/`<input type="time">` require, never
    // routed through I18n (`AdminStaysController#stay_props` shape).
    flightNumber: string | null
    origin: string | null
    destination: string | null
    departsOn: string | null
    departsAtTime: string | null
    arrivesOn: string | null
    arrivesAtTime: string | null
    note: string | null
    // t022 `impl-pill-widget` (FR-7.2): `[]` on a new record, never
    // `null` -- the framework's own `has_many :passengers` collection-ids
    // reader (`Flights::AdminFlightsController#flight_props`).
    passengerIds: number[]
  }
  // t022 `impl-pill-widget` (FR-7.2): every user, unfiltered, ordered by
  // name -- a user already flying stays selectable, since two people
  // flying the same plane is two ordinary join rows (FR-1.5), not a
  // collision.
  passengerOptions: { value: number; label: string }[]
  labels: AdminFlightFormLabels
}

// The form's own local state stays flat, keyed by the model's bare
// attribute names (the `AdminContactForm`/`AdminStayForm` shape): the
// server's failed-save `errors` prop round-trips under these same bare
// keys, and the wire payload nests them under "flight" only at submit
// time (via `transform`). t022 `impl-pill-widget`: `passenger_ids` is
// this form's only array-valued field -- the DOM (real, uncontrolled
// checkboxes) owns the toggle and its appearance; this state exists
// purely to carry the submitted set, updated by each pill's own
// `onChange` (FR-7.4).
interface FlightFormData {
  flight_number: string
  origin: string
  destination: string
  departs_on: string
  departs_at_time: string
  arrives_on: string
  arrives_at_time: string
  note: string
  passenger_ids: number[]
  authenticity_token: string
}

const INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-[15px] text-gray-900"
const BOUNDARY_DATE_CLASSES = `${INPUT_CLASSES} grow tabular-nums`
const BOUNDARY_TIME_CLASSES = `${INPUT_CLASSES} w-26 tabular-nums`
const LABEL_TEXT_CLASSES = "text-[13px] font-medium leading-[18px] text-gray-700"
const OPTIONAL_LABEL_CLASSES = "flex items-baseline gap-2 text-[13px] font-medium leading-[18px] text-gray-700"
const HINT_CLASSES = "text-xs leading-4 text-gray-400"
const ERROR_CLASSES = "text-xs leading-4 text-red-700"

// The flight form (`Flights::AdminFlightsController#new/#edit`,
// docs/design/flights.html's Block 4 verbatim): create and edit share
// this one component, the heading and the delete button's presence being
// the only differences (both server-computed, FR-6.1) -- structurally
// `AdminStayForm.tsx` minus the links fieldset, plus the «Кто летит»
// pill row (t022, below). No client-side format check runs before
// submit -- the server is the single source of validity (FR-2.2), and
// every field's value is handed to `useForm` and back to the server
// untouched.
export default function AdminFlightForm({
  heading, backHref, backLabel, submitHref, deleteHref, flight, passengerOptions, labels,
}: AdminFlightFormProps) {
  const form = useForm<FlightFormData>({
    flight_number: flight.flightNumber ?? "",
    origin: flight.origin ?? "",
    destination: flight.destination ?? "",
    departs_on: flight.departsOn ?? "",
    departs_at_time: flight.departsAtTime ?? "",
    arrives_on: flight.arrivesOn ?? "",
    arrives_at_time: flight.arrivesAtTime ?? "",
    note: flight.note ?? "",
    passenger_ids: flight.passengerIds,
    authenticity_token: csrfToken(),
  })

  form.transform((data) => ({
    flight: {
      flight_number: data.flight_number, origin: data.origin,
      destination: data.destination, departs_on: data.departs_on,
      departs_at_time: data.departs_at_time, arrives_on: data.arrives_on,
      arrives_at_time: data.arrives_at_time, note: data.note,
      passenger_ids: data.passenger_ids,
    },
    authenticity_token: data.authenticity_token,
  }))

  // t023 (NFR-1): following is armed only on the new-flight form --
  // `deleteHref === null` already selects POST over PATCH above (the same
  // discriminator), so no new prop is needed for this.
  const followsDepartureDate = deleteHref === null
  // t023 (FR-5, FR-6): a plain boolean, initially uncut, flipped only by
  // `changeArrivalDate` below -- never derived from comparing the two
  // rendered dates.
  const [arrivalDateTouched, setArrivalDateTouched] = useState(false)

  // t023 (FR-4, FR-7, NFR-1): the departure date's own change handler
  // copies its raw value into arrives_on verbatim -- no arithmetic, no
  // normalisation, the empty value included -- while the link is armed
  // and not yet cut. One setData write per event, over a full copy of
  // form.data: two keyed setData calls would only be safe under a
  // functional updater, which is unverifiable here and unfalsifiable
  // under this file's mocked-useForm test idiom.
  function changeDepartureDate(value: string) {
    const next = { ...form.data, departs_on: value }

    form.setData(followsDepartureDate && !arrivalDateTouched ? { ...next, arrives_on: value } : next)
  }

  // t023 (FR-5): any change to the arrival date's own value cuts the
  // link, unconditionally -- including on the edit form, where it is
  // never read.
  function changeArrivalDate(value: string) {
    setArrivalDateTouched(true)
    form.setData("arrives_on", value)
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (deleteHref === null) {
      form.post(submitHref)
    } else {
      form.patch(submitHref)
    }
  }

  function handleDelete(href: string) {
    if (window.confirm(labels.deleteConfirm)) {
      form.delete(href)
    }
  }

  // t022 `impl-pill-widget` (FR-7.4): the pill's checked appearance
  // (`has-checked:`/`peer-checked:`) and its toggle both come from the
  // browser's own checkbox -- this handler only records the tick into
  // `useForm` state for the eventual submit; it never drives `checked`
  // or computes a class. Reads `form.data.passenger_ids` fresh on every
  // call, so it never goes stale across renders.
  function togglePassenger(userId: number, checked: boolean) {
    form.setData(
      "passenger_ids",
      checked
        ? [...form.data.passenger_ids, userId]
        : form.data.passenger_ids.filter((id) => id !== userId),
    )
  }

  return (
    <AdminLayout>
      <Link href={backHref} className="flex items-center gap-1.5 text-sm text-accent">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {backLabel}
      </Link>
      <h2 className="text-[22px] font-bold leading-7 text-gray-900">{heading}</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* «Кто летит»: docs/design/flights.html Block 4 verbatim, minus
            its own hand cursor class (restored scoped in the entry
            stylesheet, see application.css) -- a real, sr-only checkbox per user
            inside its own label, the label's `has-checked:` variants and
            the icon's `peer-checked:block` the only thing that decides
            the ticked look (FR-7.2, FR-7.4). No `role`, `aria-checked` or
            `aria-pressed` anywhere (FR-7.5) -- the input's own semantics
            are the whole story. Uncontrolled: `defaultChecked` seeds the
            DOM once from the flight's own `passengerIds`; the pill's
            `onChange` only records the tick (`togglePassenger`, above). */}
        <fieldset className="flex flex-col gap-1.5">
          <legend className={LABEL_TEXT_CLASSES}>{labels.whoLabel}</legend>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {passengerOptions.map((option) => (
              <label
                key={option.value}
                className="inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-full border border-gray-300 bg-white px-4 text-[15px] font-medium text-gray-900 has-checked:border-accent has-checked:bg-accent/5 has-checked:text-accent has-focus-visible:ring-2 has-focus-visible:ring-accent/40"
              >
                <input
                  type="checkbox"
                  name="flight[passenger_ids][]"
                  value={option.value}
                  defaultChecked={flight.passengerIds.includes(option.value)}
                  onChange={(event) => togglePassenger(option.value, event.target.checked)}
                  className="peer sr-only"
                />
                <svg
                  aria-hidden="true"
                  className="hidden shrink-0 peer-checked:block"
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M3 8.5l3.2 3L13 4.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="truncate">{option.label}</span>
              </label>
            ))}
          </div>
          {/* t022 `impl-passenger-rule` (FR-2.2, AC-5): the refusal's own
              line, docs/design/flights.html:423 verbatim -- rendered
              under the pill row, inside the fieldset, from the model's
              own `:passenger_ids` message. Nothing here empties or
              retouches `form.data.passenger_ids`: the same-component
              redirect keeps this component mounted, so the pills stay
              exactly as the admin left them (FR-3.1, FR-3.2) and every
              other field's own `form.data`/`form.errors` render as
              usual. */}
          {form.errors.passenger_ids && (
            <span className={ERROR_CLASSES}>{form.errors.passenger_ids}</span>
          )}
        </fieldset>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.flightNumberLabel}</span>
          <input
            type="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            value={form.data.flight_number}
            onChange={(event) => form.setData("flight_number", event.target.value)}
            className={INPUT_CLASSES}
          />
          {form.errors.flight_number && (
            <span className={ERROR_CLASSES}>{form.errors.flight_number}</span>
          )}
        </label>
        <div className="flex gap-2.5">
          <label className="flex grow flex-col gap-1.5">
            <span className={LABEL_TEXT_CLASSES}>{labels.originLabel}</span>
            <input
              type="text"
              value={form.data.origin}
              onChange={(event) => form.setData("origin", event.target.value)}
              className={`${INPUT_CLASSES} w-full`}
            />
            {form.errors.origin && <span className={ERROR_CLASSES}>{form.errors.origin}</span>}
          </label>
          <label className="flex grow flex-col gap-1.5">
            <span className={LABEL_TEXT_CLASSES}>{labels.destinationLabel}</span>
            <input
              type="text"
              value={form.data.destination}
              onChange={(event) => form.setData("destination", event.target.value)}
              className={`${INPUT_CLASSES} w-full`}
            />
            {form.errors.destination && (
              <span className={ERROR_CLASSES}>{form.errors.destination}</span>
            )}
          </label>
        </div>
        {/* Вылет: a date input paired with a time input, side by side --
            two schema columns, not a datetime. The visible caption names
            the pair; each input carries its own aria-label. */}
        <div className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.departsLabel}</span>
          <div className="flex gap-2.5">
            <input
              type="date"
              aria-label={labels.departsDateAria}
              value={form.data.departs_on}
              onChange={(event) => changeDepartureDate(event.target.value)}
              className={BOUNDARY_DATE_CLASSES}
            />
            <input
              type="time"
              aria-label={labels.departsTimeAria}
              value={form.data.departs_at_time}
              onChange={(event) => form.setData("departs_at_time", event.target.value)}
              className={BOUNDARY_TIME_CLASSES}
            />
          </div>
          <span className={HINT_CLASSES}>{labels.departsHint}</span>
          {form.errors.departs_on && (
            <span className={ERROR_CLASSES}>{form.errors.departs_on}</span>
          )}
          {form.errors.departs_at_time && (
            <span className={ERROR_CLASSES}>{form.errors.departs_at_time}</span>
          )}
        </div>
        {/* Прилёт: the same shape as Вылет above. FR-2.1's one business
            rule (arrival not before departure) attaches its error to
            arrives_on server-side, so a failed save's message renders
            here. */}
        <div className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.arrivesLabel}</span>
          <div className="flex gap-2.5">
            <input
              type="date"
              aria-label={labels.arrivesDateAria}
              value={form.data.arrives_on}
              onChange={(event) => changeArrivalDate(event.target.value)}
              className={BOUNDARY_DATE_CLASSES}
            />
            <input
              type="time"
              aria-label={labels.arrivesTimeAria}
              value={form.data.arrives_at_time}
              onChange={(event) => form.setData("arrives_at_time", event.target.value)}
              className={BOUNDARY_TIME_CLASSES}
            />
          </div>
          <span className={HINT_CLASSES}>{labels.arrivesHint}</span>
          {form.errors.arrives_on && (
            <span className={ERROR_CLASSES}>{form.errors.arrives_on}</span>
          )}
          {form.errors.arrives_at_time && (
            <span className={ERROR_CLASSES}>{form.errors.arrives_at_time}</span>
          )}
        </div>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.noteLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <input
            type="text"
            value={form.data.note}
            onChange={(event) => form.setData("note", event.target.value)}
            className={INPUT_CLASSES}
          />
        </label>
        <button
          type="submit"
          disabled={form.processing}
          className="mt-2 h-12 rounded-xl bg-accent text-[15px] font-semibold text-white disabled:opacity-60"
        >
          {labels.save}
        </button>
        {deleteHref !== null && (
          <button
            type="button"
            onClick={() => handleDelete(deleteHref)}
            className="flex items-center justify-center gap-1.5 py-1 text-sm font-medium text-red-700"
          >
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path
                d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9a1.5 1.5 0 001.5 1.4h3.6A1.5 1.5 0 0011.3 13l.7-9M6.7 7v4.5M9.3 7v4.5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {labels.delete}
          </button>
        )}
      </form>
    </AdminLayout>
  )
}
